import { PageController } from '@client/components/_core/layout/PageController';
import { commKindButtonStyle } from '@client/components/_shared/comms/channel-colors';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { EmptyState } from '@client/components/_ui/feedback/EmptyState';
import { InlineQueryError } from '@client/components/_ui/feedback/InlineQueryError';
import { NoteEdit } from '@client/components/claims/NoteEdit';
import { formatSubmissionReference } from '@client/components/lodgement/hosted/lodgement-hosted';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { useFormatter } from '@client/hooks/utils/useFormatter';
import type { RouterInputs } from '@client/trpc/client';
import { createFileRoute, Outlet } from '@tanstack/react-router';
import { FileText, Inbox, Link2, Phone, Pin, RefreshCw, Search } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { claimFormStore } from '../store';
import { useClaimInteractionAccess } from '../useClaimInteractionAccess';
import { ActivityRow } from './ActivityRow';
import { emptyActivityTitle, groupByDay } from './activity-shared';
import { CommLogFilters } from './CommLogFilters';

export const Route = createFileRoute('/console/$orgId/claims/details/$id/activity')({
    component: ActivityPage,
});

type NoteDraft = { open: boolean; noteId?: bigint };

const PAGE_SIZE = 10;

function normaliseForFilterCompare(value: unknown): unknown {
    if (typeof value === 'bigint') {
        return value.toString();
    }
    if (value instanceof Date) {
        return value.toISOString();
    }
    if (Array.isArray(value)) {
        return value.map(normaliseForFilterCompare);
    }
    if (value && typeof value === 'object') {
        return Object.fromEntries(
            Object.entries(value)
                .sort(([left], [right]) => left.localeCompare(right))
                .map(([key, item]) => [key, normaliseForFilterCompare(item)]),
        );
    }
    return value;
}

function filtersEqual(
    left: RouterInputs['communication']['listCommLogAndNotes']['where'],
    right: RouterInputs['communication']['listCommLogAndNotes']['where'],
): boolean {
    return (
        JSON.stringify(normaliseForFilterCompare(left ?? {})) === JSON.stringify(normaliseForFilterCompare(right ?? {}))
    );
}

function ActivityPage() {
    return (
        <PageController route={Route}>
            <Activity />
        </PageController>
    );
}

function Activity() {
    const orgId = useOrgId();
    const { id } = Route.useParams();
    const { trpc, trpcUtils } = useTrpc();
    const navigate = useNavigate();
    // CUR-725: closed claims are read-only — no add/edit affordances in the feed.
    const { canCreate, canEdit } = useClaimInteractionAccess();
    const { formatDateTime } = useFormatter();
    // A claim lodged through a lodgement link (products v2): its oldest event, shown at the feed's end.
    const lodgement = claimFormStore.use.formData().LodgementSubmission;

    const prevWhereRef = useRef<RouterInputs['communication']['listCommLogAndNotes']['where']>({});
    const [initialLoad, setInitialLoad] = useState(true);
    const [isFiltering, setIsFiltering] = useState(false);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [noteDraft, setNoteDraft] = useState<NoteDraft>({ open: false });
    const [input, setInput] = useState<RouterInputs['communication']['listCommLogAndNotes']>({
        claimId: id,
        limit: PAGE_SIZE,
        offset: 0,
        where: {},
    });

    const activityQuery = trpc.communication.listCommLogAndNotes.useQuery(input, {
        refetchInterval: 10000,
        meta: { errorSurface: 'inline' },
    });
    const { data, isPending, error } = activityQuery;

    // Derive the list straight from the query result. Mirroring `data.rows` into local state via
    // an effect re-rendered on every unstable `data` reference and caused an infinite update loop.
    const messages = data?.rows ?? [];

    useEffect(() => {
        if (!isPending) {
            setInitialLoad(false);
            setIsFiltering(false);
            setIsLoadingMore(false);
        }
    }, [isPending]);

    const handleFiltersChange = useCallback(
        (newFilters: RouterInputs['communication']['listCommLogAndNotes']['where']) => {
            const filtersChanged = !filtersEqual(newFilters, prevWhereRef.current);
            if (filtersChanged && !initialLoad) {
                setIsFiltering(true);
            }
            prevWhereRef.current = newFilters;
            setInput((prev) => ({ ...prev, where: newFilters, offset: 0 }));
        },
        [initialLoad],
    );

    const refresh = () => {
        setInput((prev) => ({ ...prev, limit: PAGE_SIZE, offset: 0 }));
        void activityQuery.refetch();
    };
    const closeNote = () => {
        setNoteDraft({ open: false });
        void trpcUtils.communication.listCommLogAndNotes.invalidate();
    };
    const logCall = () =>
        navigate({ to: '/console/$orgId/claims/details/$id/activity/add/phone', params: { orgId, id } });
    const editCallLog = (callLogId: bigint) =>
        navigate({
            to: '/console/$orgId/claims/details/$id/activity/edit/phone/$callLogId',
            params: { orgId, id, callLogId },
        });

    const pinnedMessages = messages.filter((msg) => msg.recordType === 'note' && msg.isPinned);
    const regularMessages = messages.filter((msg) => !(msg.recordType === 'note' && msg.isPinned));
    const dayGroups = groupByDay(regularMessages);

    const clearFilters = () => {
        setIsFiltering(true);
        prevWhereRef.current = {};
        setInput((prev) => ({ ...prev, where: {}, offset: 0 }));
    };

    const hasActiveFilters = Boolean(
        input.where?.searchText ||
            input.where?.communicationType?.length ||
            input.where?.direction ||
            input.where?.userIds?.length ||
            input.where?.contactTypes?.length ||
            input.where?.toEmails?.length ||
            input.where?.fromEmails?.length ||
            input.where?.labelIds?.length ||
            input.where?.dateRange,
    );

    const sectionHeader =
        'sticky top-0 z-[1] border-separator border-t border-separator-secondary border-b bg-card px-4 pt-[9px] pb-[7px] font-bold text-2xs text-subtle-foreground uppercase tracking-wide';

    function renderBody() {
        if (initialLoad || isFiltering) {
            return (
                <div>
                    {[0, 1, 2, 3, 4].map((i) => (
                        <div
                            key={i}
                            className="flex animate-pulse items-center gap-[11px] border-separator-secondary border-b px-4 py-[14px]"
                        >
                            <span className="size-[15px] rounded bg-muted" />
                            <span className="h-5 w-[54px] rounded-[5px] bg-muted" />
                            <span className="h-2.5 flex-1 rounded bg-muted" />
                            <span className="h-2.5 w-[42px] rounded bg-muted" />
                        </div>
                    ))}
                </div>
            );
        }

        if (error) {
            return <InlineQueryError error={error} onRetry={() => void activityQuery.refetch()} />;
        }

        if (!messages.length) {
            return hasActiveFilters ? (
                <EmptyState
                    icon={<Search />}
                    title="No activity found matching your filters"
                    description="Try widening the date range or clearing a filter or two."
                    action={
                        <Button type="primary" size="small" onClick={clearFilters}>
                            Clear filters
                        </Button>
                    }
                />
            ) : (
                <EmptyState
                    icon={<Inbox />}
                    title={emptyActivityTitle(Boolean(lodgement))}
                    description="Notes, calls, emails and SMS on this claim will appear here, newest first."
                    action={
                        !canCreate ? null : (
                            <div className="flex gap-2">
                                <Button
                                    type="secondary"
                                    size="small"
                                    icon={<FileText />}
                                    onClick={() => setNoteDraft({ open: true })}
                                >
                                    Add note
                                </Button>
                                <Button type="secondary" size="small" icon={<Phone />} onClick={logCall}>
                                    Log call
                                </Button>
                            </div>
                        )
                    }
                />
            );
        }

        return (
            <div>
                {pinnedMessages.length > 0 && (
                    <>
                        <div
                            className="flex items-center gap-[7px] px-4 pt-[9px] pb-[7px]"
                            style={{
                                background: 'color-mix(in srgb, var(--color-status-warning) 6%, var(--color-card))',
                            }}
                        >
                            <Pin size={12} style={{ color: 'var(--color-yellow-800)' }} />
                            <span
                                className="font-bold text-2xs uppercase tracking-wide"
                                style={{ color: 'var(--color-yellow-800)' }}
                            >
                                Pinned
                            </span>
                        </div>
                        {pinnedMessages.map((item) => (
                            <ActivityRow
                                key={item.id.toString()}
                                item={item}
                                pinned
                                onEdit={!canEdit ? undefined : (noteId) => setNoteDraft({ open: true, noteId })}
                                onEditCallLog={!canEdit ? undefined : editCallLog}
                                readOnly={!canEdit}
                            />
                        ))}
                    </>
                )}

                {dayGroups.map((group) => (
                    <section key={group.key}>
                        <div className={sectionHeader} style={{ letterSpacing: '.04em' }}>
                            {group.label}
                        </div>
                        {group.items.map((item) => (
                            <ActivityRow
                                key={item.id.toString()}
                                item={item}
                                onEdit={!canEdit ? undefined : (noteId) => setNoteDraft({ open: true, noteId })}
                                onEditCallLog={!canEdit ? undefined : editCallLog}
                                readOnly={!canEdit}
                            />
                        ))}
                    </section>
                ))}

                {(data?.total ?? 0) > messages.length && (
                    <div className="flex justify-center p-3.5">
                        <Button
                            type="secondary"
                            size="small"
                            loading={isLoadingMore}
                            onClick={() => {
                                setIsLoadingMore(true);
                                setInput((prev) => ({
                                    ...prev,
                                    limit: (prev.limit ?? PAGE_SIZE) + PAGE_SIZE,
                                    offset: 0,
                                }));
                            }}
                        >
                            Show more
                        </Button>
                    </div>
                )}
            </div>
        );
    }

    return (
        <>
            {/* Overlay child routes (log call, edit call log) layer on top. */}
            <Outlet />

            <NoteEdit
                entity="claim"
                entityId={id}
                type="NOTE"
                title={noteDraft.noteId ? 'Edit note' : 'Add note'}
                open={noteDraft.open && (noteDraft.noteId ? canEdit : canCreate)}
                canCreateLabels={canEdit}
                onClose={closeNote}
                noteId={noteDraft.noteId}
            />

            <div className="mx-auto max-w-[760px] overflow-hidden rounded-[10px] border border-separator bg-card">
                {/* Filter bar */}
                <div className="flex items-center gap-2.5 border-separator border-b px-3.5 py-3">
                    <div className="min-w-0 flex-1">
                        <CommLogFilters claimId={id} onChange={handleFiltersChange} />
                    </div>
                    <Button
                        type="tertiary"
                        size="small"
                        iconOnly
                        icon={<RefreshCw />}
                        title="Refresh"
                        onClick={refresh}
                    />
                </div>

                {/* Quick add — withheld on a closed (read-only) claim (CUR-725). */}
                {canCreate && (
                    <div className="flex items-center gap-2 border-separator border-b bg-muted px-3.5 py-2.5">
                        <span className="mr-0.5 text-subtle-foreground text-xs">Add to feed</span>
                        <button
                            type="button"
                            onClick={() => setNoteDraft({ open: true })}
                            className="inline-flex h-7 cursor-pointer items-center gap-1.5 rounded-[7px] px-[11px] font-semibold text-xs"
                            style={commKindButtonStyle('note')}
                        >
                            <FileText size={13} /> Note
                        </button>
                        <button
                            type="button"
                            onClick={logCall}
                            className="inline-flex h-7 cursor-pointer items-center gap-1.5 rounded-[7px] px-[11px] font-semibold text-xs"
                            style={commKindButtonStyle('call')}
                        >
                            <Phone size={13} /> Log call
                        </button>
                    </div>
                )}

                {renderBody()}

                {lodgement && !hasActiveFilters && !error && !initialLoad && (data?.total ?? 0) <= messages.length && (
                    <div className="flex items-center gap-[11px] border-separator-secondary border-t bg-muted px-4 py-3 text-sm">
                        <Link2 size={15} className="shrink-0 text-subtle-foreground" />
                        <span className="min-w-0 flex-1 text-foreground">
                            Lodged via link “{lodgement.Link.name}”
                            <span className="ml-2 font-mono text-subtle-foreground text-xs">
                                {formatSubmissionReference(lodgement.publicId)}
                            </span>
                        </span>
                        {lodgement.submittedAt && (
                            <span className="shrink-0 text-subtle-foreground text-xs">
                                {formatDateTime(lodgement.submittedAt)}
                            </span>
                        )}
                    </div>
                )}
            </div>
        </>
    );
}
